import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BACKEND, api, fmtDate, money } from "@/lib/api";

const monthName = (p) => new Date(`${p}-15T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
import { Button } from "@/components/ui/button";
import { PrintButton } from "@/components/PrintButton";
import { Download } from "lucide-react";

const COLS = [["commission_gross", "Commission (10%)"], ["commission_refunded", "Returned on refunds"], ["commission_net", "Net commission"],
  ["host_pro", "Host Pro"], ["total_income", "Total income"], ["tax_held_not_income", "Tax held (not income)"]];

// FairStay's own business income by month, kept separate from any lodging tax.
export function AdminIncome() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useQuery({ queryKey: ["admin-income", year], queryFn: () => api.get(`/admin/income?year=${year}`).then((r) => r.data) });
  if (!data) return null;
  return (
    <section id="admin-income-report" className="rounded-3xl border bg-white p-6" data-testid="admin-income">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-lg font-semibold">FairStay business income {year}</h2><p className="text-xs text-stone-500">For FairStay's own income taxes, grouped by payment month. Lodging tax is never counted here. Test bookings are excluded.</p></div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <select className="rounded-xl border px-3 py-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="income-year">
            {[0, 1, 2].map((d) => new Date().getFullYear() - d).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="income-download"><a href={`${BACKEND}/api/admin/income?year=${year}&format=csv`}><Download className="mr-2 h-4 w-4" />Spreadsheet</a></Button>
          <PrintButton targetId="admin-income-report" testid="income-print" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="income-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500"><tr><th className="pb-2">Month</th><th className="pb-2">Stays</th>{COLS.map(([k, l]) => <th key={k} className="pb-2">{l}</th>)}</tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.month} className="border-t" data-testid={`income-row-${r.month}`}>
                <td className="py-2 pr-4">{r.month}</td><td className="py-2 pr-4">{r.stays}</td>
                {COLS.map(([k]) => <td key={k} className={`py-2 pr-4 font-mono-num ${k === "total_income" ? "font-semibold" : ""}`}>{money(r[k])}</td>)}
              </tr>
            ))}
            <tr className="border-t-2 font-semibold"><td className="py-2">Total</td><td className="py-2">{data.totals.stays}</td>
              {COLS.map(([k]) => <td key={k} className="py-2 font-mono-num" data-testid={`income-total-${k}`}>{money(data.totals[k])}</td>)}</tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Homeowner's own lodging-tax reminder for last month's stays.
export function HostTaxReminder() {
  const { data: nf } = useQuery({ queryKey: ["host-tax-reminder"], queryFn: () => api.get("/host/tax-reminder").then((r) => r.data) });
  if (!nf || !nf.stays) return null;
  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="host-tax-reminder">
      <p className="font-semibold">Lodging-tax return for {monthName(nf.period)} stays due {fmtDate(nf.due)} · {nf.days_left} days left</p>
      <p>Your guests paid <span className="font-mono-num font-semibold" data-testid="host-tax-reminder-amount">{money(nf.host_collected)}</span> in lodging tax for those stays. You're intended to file and pay it where legally permitted.</p>
      <ul className="mt-1 text-xs">{nf.areas.filter((a) => a.host_collected > 0).map((a) => <li key={a.area}>{a.area}: {money(a.host_collected)}</li>)}</ul>
      <p className="mt-1 text-xs">{nf.approval} {nf.note}</p>
    </div>
  );
}
